/** Cached plans decide immediately; an unavailable check never takes away Capture. */
import { useEffect, useState } from "react";
import { ENTITLEMENT_CHANGED, readCachedEntitlement, refreshEntitlement, type CachedEntitlement } from "@/lib/entitlement";
export const PLAN_REFRESH_TIMEOUT_MS = 5_000;
export const PLAN_CHECK_WARNING = "Couldn't check your plan — you can keep logging";
export function needsPlan(cache: CachedEntitlement | null): boolean { return cache?.entitlement.state === "none"; }
export function usePlanGate(accountId: string | null): { loading: boolean; needsPlan: boolean; error: string | null; retry: () => void; entitlement: CachedEntitlement["entitlement"] | null } {
  const [cache, setCache] = useState<CachedEntitlement | null>(null);
  const [checkedAccount, setCheckedAccount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    const controller = new AbortController();
    const stored = readCachedEntitlement(accountId);
    setCache(stored); setError(null);
    if (stored) setCheckedAccount(accountId);
    const timer = window.setTimeout(() => {
      controller.abort();
      if (alive) { setCheckedAccount(accountId); setError(stored ? null : PLAN_CHECK_WARNING); }
    }, PLAN_REFRESH_TIMEOUT_MS);
    const update = () => { if (alive) setCache(readCachedEntitlement(accountId)); };
    window.addEventListener(ENTITLEMENT_CHANGED, update);
    void refreshEntitlement(undefined, undefined, controller.signal).then((value) => {
      if (!alive || controller.signal.aborted) return;
      window.clearTimeout(timer);
      setCache(value); setCheckedAccount(accountId); setError(null);
    }).catch((cause: unknown) => {
      console.warn("Could not check the plan:", cause);
      if (!alive || controller.signal.aborted) return;
      window.clearTimeout(timer);
      const stored = readCachedEntitlement(accountId);
      setCache(stored); setCheckedAccount(accountId);
      setError(stored ? null : PLAN_CHECK_WARNING);
    });
    return () => { alive = false; controller.abort(); window.clearTimeout(timer); window.removeEventListener(ENTITLEMENT_CHANGED, update); };
  }, [accountId, attempt]);
  const current = cache?.accountId === accountId ? cache : accountId ? readCachedEntitlement(accountId) : null;
  return { loading: accountId !== null && accountId !== checkedAccount && current === null, needsPlan: needsPlan(current), entitlement: current?.entitlement ?? null, error: accountId === checkedAccount ? error : null, retry: () => setAttempt((value) => value + 1) };
}
